import {
  type CalendarError,
  CalendarErrorType,
  type CalendarEvent,
  type CalendarSyncResult,
} from '../types/calendar';
import { googleCalendarService } from './googleCalendarService';
import { microsoftCalendarService } from './microsoftCalendarService';

export interface SyncOptions {
  forceRefresh?: boolean;
  showLoadingIndicator?: boolean;
}

export interface SyncState {
  isLoading: boolean;
  lastSync?: Date;
  error?: CalendarError;
}

export class CalendarSyncService {
  private syncIntervals: Map<string, NodeJS.Timeout> = new Map();
  private syncStates: Map<string, SyncState> = new Map();
  private readonly SYNC_INTERVAL = 15 * 60 * 1000; // 15 minutes

  /**
   * Start periodic background sync for a user
   * Requirement: Add periodic background sync every 15 minutes
   */
  public startPeriodicSync(userId: string): void {
    // Clear existing interval if any
    this.stopPeriodicSync(userId);

    const intervalId = setInterval(async () => {
      try {
        console.log(`[CalendarSyncService] Starting periodic sync for user ${userId}`);
        await this.syncAllCalendars(userId, { showLoadingIndicator: false });
      } catch (error) {
        console.error(`[CalendarSyncService] Periodic sync failed for user ${userId}:`, error);
      }
    }, this.SYNC_INTERVAL);

    this.syncIntervals.set(userId, intervalId);
    console.log(`[CalendarSyncService] Started periodic sync for user ${userId}`);
  }

  /**
   * Stop periodic background sync for a user
   */
  public stopPeriodicSync(userId: string): void {
    const intervalId = this.syncIntervals.get(userId);
    if (intervalId) {
      clearInterval(intervalId);
      this.syncIntervals.delete(userId);
      console.log(`[CalendarSyncService] Stopped periodic sync for user ${userId}`);
    }
  }

  /**
   * Manual refresh functionality with loading indicators
   * Requirement: Implement manual refresh functionality with loading indicators
   */
  public async manualRefresh(userId: string): Promise<CalendarSyncResult> {
    console.log(`[CalendarSyncService] Manual refresh initiated for user ${userId}`);

    // Set loading state
    this.setSyncState(userId, { isLoading: true });

    try {
      const result = await this.syncAllCalendars(userId, {
        forceRefresh: true,
        showLoadingIndicator: true,
      });

      // Update sync state with success
      this.setSyncState(userId, {
        isLoading: false,
        lastSync: new Date(),
        error: undefined,
      });

      return result;
    } catch (error) {
      const calendarError: CalendarError = {
        type: CalendarErrorType.SYNC_FAILED,
        message: error instanceof Error ? error.message : 'Manual refresh failed',
        retryable: true,
      };

      // Update sync state with error
      this.setSyncState(userId, {
        isLoading: false,
        error: calendarError,
      });

      throw calendarError;
    }
  }

  /**
   * Sync all connected calendars for a user
   * Requirement: Create syncEvents methods for both Google and Microsoft calendar services
   */
  public async syncAllCalendars(
    userId: string,
    _options: SyncOptions = {},
  ): Promise<CalendarSyncResult> {
    const startTime = new Date();
    let totalEvents = 0;
    const errors: string[] = [];

    try {
      // Get all calendar connections for the user
      const googleConnection = googleCalendarService.getGoogleConnection(userId);
      const microsoftConnection = microsoftCalendarService.getMicrosoftConnection(userId);

      if (!(googleConnection || microsoftConnection)) {
        throw new Error('No calendar connections found for user');
      }

      // Sync Google Calendar if connected
      if (googleConnection) {
        try {
          const googleEvents = await googleCalendarService.syncEvents(userId);
          totalEvents += googleEvents.length;
          console.log(`[CalendarSyncService] Synced ${googleEvents.length} Google events`);
        } catch (error) {
          const errorMsg = `Google Calendar sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error('[CalendarSyncService]', errorMsg);
        }
      }

      // Sync Microsoft Calendar if connected
      if (microsoftConnection) {
        try {
          const microsoftEvents = await microsoftCalendarService.syncEvents(userId);
          totalEvents += microsoftEvents.length;
          console.log(`[CalendarSyncService] Synced ${microsoftEvents.length} Microsoft events`);
        } catch (error) {
          const errorMsg = `Microsoft Calendar sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error('[CalendarSyncService]', errorMsg);
        }
      }

      const result: CalendarSyncResult = {
        success: errors.length === 0,
        eventsCount: totalEvents,
        lastSync: startTime,
        error: errors.length > 0 ? errors.join('; ') : undefined,
      };

      console.log(`[CalendarSyncService] Sync completed for user ${userId}:`, result);
      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown sync error';
      console.error(`[CalendarSyncService] Sync failed for user ${userId}:`, errorMsg);

      return {
        success: false,
        eventsCount: 0,
        lastSync: startTime,
        error: errorMsg,
      };
    }
  }

  /**
   * Get all events from all connected calendars
   * Requirement: Build event caching and local storage management
   */
  public async getAllEvents(userId: string): Promise<CalendarEvent[]> {
    const allEvents: CalendarEvent[] = [];

    try {
      // Get Google Calendar events (from cache or fresh sync)
      const googleConnection = googleCalendarService.getGoogleConnection(userId);
      if (googleConnection) {
        const googleEvents = await googleCalendarService.getCachedEvents(userId);
        allEvents.push(...googleEvents);
      }

      // Get Microsoft Calendar events (from cache or fresh sync)
      const microsoftConnection = microsoftCalendarService.getMicrosoftConnection(userId);
      if (microsoftConnection) {
        const microsoftEvents = await microsoftCalendarService.getCachedEvents(userId);
        allEvents.push(...microsoftEvents);
      }

      // Sort events by start time
      allEvents.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      // Remove duplicates based on title and start time (in case of cross-platform events)
      const uniqueEvents = this.removeDuplicateEvents(allEvents);

      console.log(
        `[CalendarSyncService] Retrieved ${uniqueEvents.length} total events for user ${userId}`,
      );
      return uniqueEvents;
    } catch (error) {
      console.error(`[CalendarSyncService] Failed to get all events for user ${userId}:`, error);
      return [];
    }
  }

  /**
   * Remove duplicate events that might exist across providers
   */
  private removeDuplicateEvents(events: CalendarEvent[]): CalendarEvent[] {
    const seen = new Set<string>();
    return events.filter((event) => {
      // Create a unique key based on title, start time, and duration
      const key = `${event.title}_${event.startTime.getTime()}_${event.endTime.getTime()}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * Get sync state for a user
   */
  public getSyncState(userId: string): SyncState {
    return this.syncStates.get(userId) || { isLoading: false };
  }

  /**
   * Set sync state for a user
   */
  private setSyncState(userId: string, state: Partial<SyncState>): void {
    const currentState = this.getSyncState(userId);
    this.syncStates.set(userId, { ...currentState, ...state });
  }

  /**
   * Clear all cached events for a user
   */
  public async clearAllCachedEvents(userId: string): Promise<void> {
    try {
      await Promise.all([
        googleCalendarService.clearCachedEvents(userId),
        microsoftCalendarService.clearCachedEvents(userId),
      ]);
      console.log(`[CalendarSyncService] Cleared all cached events for user ${userId}`);
    } catch (error) {
      console.error(
        `[CalendarSyncService] Failed to clear cached events for user ${userId}:`,
        error,
      );
    }
  }

  /**
   * Get last sync time for a user
   */
  public getLastSyncTime(userId: string): Date | undefined {
    const googleConnection = googleCalendarService.getGoogleConnection(userId);
    const microsoftConnection = microsoftCalendarService.getMicrosoftConnection(userId);

    const syncTimes: Date[] = [];

    if (googleConnection?.lastSyncAt) {
      syncTimes.push(googleConnection.lastSyncAt);
    }

    if (microsoftConnection?.lastSyncAt) {
      syncTimes.push(microsoftConnection.lastSyncAt);
    }

    if (syncTimes.length === 0) {
      return undefined;
    }

    // Return the most recent sync time
    return new Date(Math.max(...syncTimes.map((date) => date.getTime())));
  }

  /**
   * Check if sync is needed (based on cache TTL)
   */
  public isSyncNeeded(userId: string): boolean {
    const lastSync = this.getLastSyncTime(userId);

    if (!lastSync) {
      return true; // No previous sync
    }

    const now = new Date();
    const timeSinceLastSync = now.getTime() - lastSync.getTime();
    const syncThreshold = 10 * 60 * 1000; // 10 minutes

    return timeSinceLastSync > syncThreshold;
  }

  /**
   * Initialize sync service for a user (start periodic sync if calendars are connected)
   */
  public async initializeForUser(userId: string): Promise<void> {
    try {
      const googleConnection = googleCalendarService.getGoogleConnection(userId);
      const microsoftConnection = microsoftCalendarService.getMicrosoftConnection(userId);

      if (googleConnection || microsoftConnection) {
        // Start periodic sync
        this.startPeriodicSync(userId);

        // Perform initial sync if needed
        if (this.isSyncNeeded(userId)) {
          console.log(`[CalendarSyncService] Performing initial sync for user ${userId}`);
          await this.syncAllCalendars(userId);
        }
      }
    } catch (error) {
      console.error(`[CalendarSyncService] Failed to initialize for user ${userId}:`, error);
    }
  }

  /**
   * Cleanup resources for a user (stop periodic sync)
   */
  public cleanup(userId: string): void {
    this.stopPeriodicSync(userId);
    this.syncStates.delete(userId);
    console.log(`[CalendarSyncService] Cleaned up resources for user ${userId}`);
  }

  /**
   * Cleanup all resources
   */
  public cleanupAll(): void {
    // Stop all periodic syncs
    this.syncIntervals.forEach((_, userId) => {
      this.stopPeriodicSync(userId);
    });

    // Clear all sync states
    this.syncStates.clear();

    console.log('[CalendarSyncService] Cleaned up all resources');
  }
}

// Export singleton instance
export const calendarSyncService = new CalendarSyncService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    calendarSyncService.cleanupAll();
  });
}
